/**
 * Aritmética decimal exacta para dinero (RNF-10 / N-08).
 * Redondeo half-up a 2 decimales por línea, siguiendo T-109 spec v3.0.
 * Prohibido usar `number` para cálculos monetarios en el resto del código.
 */

import { Decimal } from "decimal.js";

Decimal.set({ rounding: Decimal.ROUND_HALF_UP, precision: 20 });

export type Money = Decimal;

export const money = (v: string | number | Decimal): Money => new Decimal(v);
export const ZERO: Money = new Decimal(0);

export function round2(v: Money): Money {
  return v.toDecimalPlaces(2, Decimal.ROUND_HALF_UP);
}

export interface LineInput {
  quantity: string | number;
  listPrice: string | number;
  discountType: "percent" | "amount";
  discountValue: string | number;
  taxPercent: string | number;
}

export interface LineComputed {
  lineGross: Money;
  lineDiscount: Money;
  lineNet: Money;
  lineTax: Money;
  lineTotal: Money;
}

/** Fórmulas T-109 aplicadas por línea. */
export function computeLine(line: LineInput): LineComputed {
  const q = money(line.quantity);
  const p = money(line.listPrice);
  const dv = money(line.discountValue);
  const tp = money(line.taxPercent);

  const lineGross = round2(q.times(p));
  const rawDisc = line.discountType === "percent"
    ? round2(lineGross.times(dv).dividedBy(100))
    : Decimal.min(dv, lineGross);
  const lineDiscount = round2(rawDisc);
  const lineNet = lineGross.minus(lineDiscount);
  const lineTax = round2(lineNet.times(tp).dividedBy(100));
  const lineTotal = lineNet.plus(lineTax);
  return { lineGross, lineDiscount, lineNet, lineTax, lineTotal };
}

export interface HeaderInput {
  headerDiscountType: "percent" | "amount";
  headerDiscountValue: string | number;
  adjustment: string | number;
}

export interface HeaderComputed {
  subTotal: Money;
  headerDiscount: Money;
  discountedSubtotal: Money;
  totalTax: Money;
  totalTaxAdjusted: Money;
  adjustment: Money;
  grandTotal: Money;
}

/** Encabezado T-109: totales del pedido/cotización. */
export function computeHeader(lines: LineComputed[], header: HeaderInput): HeaderComputed {
  const subTotal = lines.reduce<Money>((acc, l) => acc.plus(l.lineNet), ZERO);
  const dv = money(header.headerDiscountValue);
  const rawHd = header.headerDiscountType === "percent"
    ? round2(subTotal.times(dv).dividedBy(100))
    : Decimal.min(dv, subTotal);
  const headerDiscount = round2(rawHd);
  const discountedSubtotal = subTotal.minus(headerDiscount);
  const totalTax = lines.reduce<Money>((acc, l) => acc.plus(l.lineTax), ZERO);
  const totalTaxAdjusted = subTotal.isZero()
    ? ZERO
    : round2(totalTax.times(discountedSubtotal).dividedBy(subTotal));
  const adjustment = money(header.adjustment);
  const grandTotal = discountedSubtotal.plus(totalTaxAdjusted).plus(adjustment);
  return { subTotal, headerDiscount, discountedSubtotal, totalTax, totalTaxAdjusted, adjustment, grandTotal };
}

/** Formato de presentación en COP: `$ 1.234.567,89` (D-005). */
export function formatCOP(v: Money): string {
  return `$ ${v.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ".").replace(/\.(\d{2})$/, ",$1")}`;
}
